import { z } from "zod/v4"

// Connector tool names are namespaced per provider at runtime and the slug is
// lossy, so the tool result carries its own identity instead of the reader
// trying to map a name back to a connector.
export const CONNECTOR_ATTRIBUTION_KEY = "__plConnector"

export const connectorAttributionSchema = z.object({
	sectionId: z.string(),
	title: z.string(),
	/** Remote MCP tool name, or the HTTP action name ("read" for reads). */
	tool: z.string(),
	/** The guard variant the call ran under, when the tool has more than one. */
	variant: z.string().optional(),
	/** "builtin" is one of our own tools calling an outside system, with no connector row. */
	kind: z.enum(["http", "mcp", "builtin"]),
	ok: z.boolean(),
})
export type ConnectorAttribution = z.infer<typeof connectorAttributionSchema>

export const MAX_CONNECTOR_ATTRIBUTIONS = 10

/** Wraps a connector tool's own return value so callers can attribute it. */
export function withConnectorAttribution<T>(
	attribution: ConnectorAttribution,
	output: T,
): { [CONNECTOR_ATTRIBUTION_KEY]: ConnectorAttribution; output: T } {
	return { [CONNECTOR_ATTRIBUTION_KEY]: attribution, output }
}

type JsonValue =
	| string
	| number
	| boolean
	| null
	| JsonValue[]
	| { [key: string]: JsonValue }

// The model must see what it saw before the envelope existed, so every
// connector tool hands this to the ai-sdk as its `toModelOutput`.
export function connectorModelOutput(
	output: unknown,
): { type: "text"; value: string } | { type: "json"; value: JsonValue } {
	const inner = unwrapConnectorOutput(output)
	return typeof inner === "string"
		? { type: "text", value: inner }
		: { type: "json", value: inner as JsonValue }
}

export function unwrapConnectorOutput(output: unknown): unknown {
	if (!isEnvelope(output)) return output
	return output.output
}

export function readConnectorAttribution(
	output: unknown,
): ConnectorAttribution | null {
	if (!isEnvelope(output)) return null
	const parsed = connectorAttributionSchema.safeParse(
		output[CONNECTOR_ATTRIBUTION_KEY],
	)
	return parsed.success ? parsed.data : null
}

export function dedupeConnectorAttributions(
	attributions: readonly ConnectorAttribution[],
	max: number = MAX_CONNECTOR_ATTRIBUTIONS,
): ConnectorAttribution[] {
	const seen = new Set<string>()
	const out: ConnectorAttribution[] = []
	for (const attribution of attributions) {
		const key = `${attribution.sectionId}::${attribution.tool}::${attribution.variant ?? ""}`
		if (seen.has(key)) continue
		seen.add(key)
		out.push(attribution)
		if (out.length >= max) break
	}
	return out
}

// `steps` is typed as unknown because the ai-sdk's StepResult is generic over
// the tool record and shifts between versions.
export function connectorAttributionsFromSteps(
	steps: unknown,
): ConnectorAttribution[] {
	if (!Array.isArray(steps)) return []
	const found: ConnectorAttribution[] = []
	for (const step of steps) {
		const toolResults = (step as { toolResults?: unknown })?.toolResults
		if (!Array.isArray(toolResults)) continue
		for (const result of toolResults) {
			const raw = result as { result?: unknown; output?: unknown }
			const attribution =
				readConnectorAttribution(raw?.output) ??
				readConnectorAttribution(raw?.result)
			if (attribution) found.push(attribution)
		}
	}
	return dedupeConnectorAttributions(found)
}

// MCP tools reach the model without schemas, so the ai-sdk emits them as
// `dynamic-tool` parts rather than `tool-<name>`.
export function connectorAttributionsFromParts(
	parts: readonly { type: string; output?: unknown }[],
): ConnectorAttribution[] {
	const found: ConnectorAttribution[] = []
	for (const part of parts) {
		if (typeof part?.type !== "string") continue
		if (!part.type.startsWith("tool-") && part.type !== "dynamic-tool") continue
		const attribution = readConnectorAttribution(part.output)
		if (attribution) found.push(attribution)
	}
	return dedupeConnectorAttributions(found)
}

function isEnvelope(
	value: unknown,
): value is { [CONNECTOR_ATTRIBUTION_KEY]: unknown; output: unknown } {
	return (
		typeof value === "object" &&
		value !== null &&
		!Array.isArray(value) &&
		CONNECTOR_ATTRIBUTION_KEY in value
	)
}
